import { Component, Input, OnInit, inject } from '@angular/core';
import {FormBuilder, FormGroup} from '@angular/forms';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {EMPTY, Observable, Subject, throwError} from 'rxjs';
import {catchError, debounceTime, filter, finalize, map, switchMap, tap} from 'rxjs/operators';

import {NotificationService} from '@shared';

import {
    AddressFinderService,
    AddressUpdateService,
    NavigationService,
    Patient,
    PatientAddressUpdate,
    PatientToAddressStatus,
    ProblemInstanceService
} from 'planner-shared';

import {PatientAddressService} from '../services/patient-address.service';
import {Address} from '../../shared/models/address.model';

@UntilDestroy()
@Component({
    selector: 'app-patient-address',
    templateUrl: './patient-address.component.html',
    styleUrls: ['./patient-address.component.scss'],
    standalone: false
})
export class PatientAddressComponent implements OnInit {
    private readonly formBuilder = inject(FormBuilder);
    private readonly problemInstanceService = inject(ProblemInstanceService);
    private readonly addressFinderService = inject(AddressFinderService);
    private readonly addressUpdateService = inject(AddressUpdateService);
    private readonly notificationService = inject(NotificationService);
    private readonly navigationService = inject(NavigationService);
    private readonly patientAddressService = inject(PatientAddressService);

    @Input()
    public set patient(patient: Patient) {
        this.saveAddress();

        this._patient = patient;
        this.setAddress();
    }

    public get patient(): Patient {
        return this._patient;
    }

    @Input() public editing: boolean;

    public readonly VALIDATE_DELAY_TIME = 800;

    public addressForm: FormGroup;
    public validating: boolean;
    public addressValid: boolean;
    public addressSuggestions: Address[];

    private _patient: Patient;
    private readonly validate$: Subject<string>;

    constructor() {
        this.addressValid = true;
        this.validate$ = new Subject<string>();
        this.addressForm = this.formBuilder.group({
            address: null,
            addressDescription: null,
            zipCodeWarning: null,
            zipCodeSuggestion: null,
            status: null,
            permanent: null
        });
    }

    ngOnInit(): void {
        this.validate$
            .pipe(
                untilDestroyed(this),
                debounceTime(this.VALIDATE_DELAY_TIME),
                switchMap(() => this.checkAddress())
            )
            .subscribe();
    }

    public validateAddress(): void {
        this.validating = true;
        this.addressValid = false;
        this.validate$.next(null);
    }

    public saveAddress(event?: Event): void {
        const patient = this.patient;

        if (event) {
            event.stopPropagation();
        }

        if (this.saveDisabled() || !this.editing || patient.addressUpdating) {
            return;
        }

        this.setUpdateState();
        this.navigationService.disableNextButton(true);

        this.patientAddressService
            .updateAddressForPatient(patient, this.addressForm)
            .pipe(
                catchError(() => {
                    this.patientAddressService.onSaveError(this.patient, this.addressForm);
                    return throwError('Update patient address failed.');
                }),
                finalize(() => this.navigationService.disableNextButton(false))
            )
            .subscribe(() => {
                patient.addressUpdating = false;
                if (this.patient.patientId === patient.patientId) {
                    this.setAddress();
                }

                this.patientAddressService.patientAddressEditStateChanged(patient);
                this.notificationService.success('CLIENT_ADDRESS_UPDATED');
            });
    }

    public approveZipCode(event: Event): void {
        event.stopPropagation();
        if (this.patient.addressUpdating) {
            return;
        }

        const patient = this.patient;
        patient.addressUpdating = true;

        const patientAddressUpdate = <PatientAddressUpdate>{
            patientId: patient.patientId,
            streetAddressNameNumberAndLetter: patient.streetAddressNameNumberAndLetter,
            addressDescription: patient.addressDescription,
            zipCode: patient.zipCodeSuggestion,
            permanent: true
        };

        this.addressUpdateService
            .updateAddressForPatient(patientAddressUpdate)
            .pipe(
                switchMap(result =>
                    this.problemInstanceService.getProblemInstance().pipe(
                        tap(problemInstanceResult => {
                            problemInstanceResult.problemInstance.officeInfo.numberOfTasksWithoutMainResponsibles =
                                result.numberOfTasksWithoutMainResponsibles;
                            problemInstanceResult.problemInstance.taskWorkShiftCompatibility =
                                result.taskWorkShiftCompatibility;
                        })
                    )
                ),
                catchError(() => {
                    this.patientAddressService.onSaveError(this.patient, this.addressForm);
                    return throwError('Update patient address failed.');
                })
            )
            .subscribe(() => {
                patient.zipCode = patientAddressUpdate.zipCode;
                patient.zipCodeSuggestion = null;
                patient.zipCodeWarning = false;
                patient.addressUpdating = false;

                if (this.patient.patientId === patient.patientId) {
                    this.setAddress();
                }

                this.patientAddressService.patientAddressEditStateChanged(patient);
                this.notificationService.success('CLIENT_ADDRESS_UPDATED');
            });
    }

    public saveDisabled(): boolean {
        return (
            this.validating ||
            (!this.addressForm.value.zipCodeWarning && (this.addressForm.pristine || !this.addressValid))
        );
    }

    public edit(event: Event): void {
        if (this.patient.taskUpdating || this.patient.feasibilityReloading) {
            return;
        }

        event.stopPropagation();
        this.editing = true;
    }

    public reset(): void {
        if (this.addressForm.dirty && !this.validating) {
            this.addressValid = this.patient.addressValid;
            this.patientAddressService.restorePatientAddress(this.patient, this.addressForm);
        }
    }

    public setAddress(): void {
        this.validating = false;
        this.addressValid = this.patient.addressValid;
        this.editing = !this.patient.addressValid || this.patient.addressUpdating;
        this.patientAddressService.initAddressForm(this.addressForm, this.patient);

        if (this.patient.addressUpdating) {
            this.setUpdateState();
            this.patientAddressService.disableAddressFormInputs(this.addressForm);
        }
    }

    public clickOutside(event?: Event): void {
        if (event) {
            event.stopPropagation();
        }

        if (this.saveDisabled() || this.patient.addressUpdating) {
            if (this.addressValid && !this.patient.addressUpdating) {
                this.editing = false;
            }
            return;
        }

        this.saveAddress();
    }

    private checkAddress(): Observable<void> {
        if (!this.validating) {
            return EMPTY;
        }

        const addressSelection: Address = this.addressForm.value.address;
        const patient = new Patient(
            this.patient.patientId, addressSelection.streetNameAndHouseNumber,
            addressSelection.zipCode, this.patient.patientName);

        return this.problemInstanceService.getProblemInstance().pipe(
            switchMap(result => {
                return this.addressFinderService.validateAddress(patient, result.problemInstance.officeInfo);
            }),
            filter(result => result.patientToAddressStatusList && result.patientToAddressStatusList.length > 0),
            map(result => {
                if (patient.patientId !== this.patient.patientId) {
                    return;
                }
                const patientAddressStatus = result.patientToAddressStatusList[0];
                const newZipCode = this.getNewZipCode(addressSelection, patientAddressStatus);
                const status = this.shouldDisplayCustomZip(addressSelection, patientAddressStatus, patient);

                this.addressForm.controls.status.setValue(patientAddressStatus.status);
                this.addressForm.controls.zipCodeWarning.setValue(status);
                this.addressForm.controls.zipCodeSuggestion.setValue(newZipCode);
                this.addressForm.controls.address.setValue(
                    <Address>{
                        streetNameAndHouseNumber: addressSelection.streetNameAndHouseNumber,
                        zipCode: newZipCode,
                        userSelectedOption: false
                    });

                this.addressSuggestions = patientAddressStatus.addressSuggestions;
                this.validating = false;
                this.addressValid = patientAddressStatus.status;
            })
        );
    }

    private shouldDisplayCustomZip(addressSelection: Address, patientAddressStatus: PatientToAddressStatus, patient: Patient): boolean {
        return addressSelection.userSelectedOption
            || patientAddressStatus.zipCodeWarning
            || addressSelection.zipCode !== patient.zipCode;
    }

    private getNewZipCode(address: Address, patientAddressStatus: PatientToAddressStatus): string {
        if (!address.userSelectedOption && patientAddressStatus.zipCodeWarning) {
            return patientAddressStatus.zipCodeSuggestion;
        }
        return address.zipCode;
    }

    private setUpdateState(): void {
        const patient = this.patient;
        setTimeout(() => (patient.addressUpdating = true));
    }
}
